import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../lib/axios'
import toast from 'react-hot-toast'

/**
 * useExecution
 *
 * Manages the full state of a test plan execution including:
 *   - Loading grouped rows from the API
 *   - Auto-saving individual row changes (debounced)
 *   - Tracking real-time progress totals
 *   - Attachment uploads
 */
export function useExecution(executionId) {
  const [execution, setExecution]   = useState(null)
  const [grouped, setGrouped]       = useState([])
  const [progress, setProgress]     = useState(0)
  const [passRate, setPassRate]     = useState(null)
  const [loading, setLoading]       = useState(true)
  const [saving, setSaving]         = useState({})   // { [rowId]: bool }
  const saveTimers = useRef({})

  // ── Load ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!executionId) return
    setLoading(true)
    try {
      const { data } = await api.get(`/executions/${executionId}`)
      setExecution(data.execution)
      setGrouped(data.grouped)
      setProgress(data.progress)
      setPassRate(data.pass_rate)
    } catch {
      toast.error('Failed to load test plan execution.')
    } finally {
      setLoading(false)
    }
  }, [executionId])

  useEffect(() => { load() }, [load])

  // ── Auto-save a row (debounced 600ms) ────────────────────────────────
  const saveRow = useCallback((rowId, updates) => {
    // Optimistically update local state immediately for snappy UI
    setGrouped(prev => prev.map(group => ({
      ...group,
      rows: group.rows.map(row =>
        row.id === rowId ? { ...row, ...updates } : row
      )
    })))

    // Debounce the API call
    if (saveTimers.current[rowId]) clearTimeout(saveTimers.current[rowId])

    setSaving(s => ({ ...s, [rowId]: true }))

    saveTimers.current[rowId] = setTimeout(async () => {
      try {
        const { data } = await api.put(
          `/executions/${executionId}/rows/${rowId}`,
          updates
        )
        // Update with server response (includes computed fields)
        setGrouped(prev => prev.map(group => ({
          ...group,
          rows: group.rows.map(row => row.id === rowId ? data.row : row),
          // Recalculate group totals
          total:   group.rows.length,
          passed:  group.rows.filter(r => (r.id === rowId ? data.row : r).status === 'pass').length,
          failed:  group.rows.filter(r => (r.id === rowId ? data.row : r).status === 'fail').length,
          pending: group.rows.filter(r => (r.id === rowId ? data.row : r).status === 'pending').length,
        })))
        setProgress(data.progress)
        setPassRate(data.pass_rate)
      } catch {
        toast.error('Auto-save failed — please retry.')
      } finally {
        setSaving(s => ({ ...s, [rowId]: false }))
      }
    }, 600)
  }, [executionId])

  // ── Upload attachment ────────────────────────────────────────────────
  const uploadAttachment = useCallback(async (rowId, file, caption = '') => {
    const formData = new FormData()
    formData.append('file', file)
    if (caption) formData.append('caption', caption)

    const toastId = toast.loading(`Uploading ${file.name}…`)
    try {
      const { data } = await api.post(
        `/executions/${executionId}/rows/${rowId}/attachments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      // Append attachment to local state
      setGrouped(prev => prev.map(group => ({
        ...group,
        rows: group.rows.map(row =>
          row.id === rowId
            ? { ...row, attachments: [...(row.attachments ?? []), data], status: row.status === 'pending' ? 'fail' : row.status }
            : row
        )
      })))
      toast.success('Screenshot attached.', { id: toastId })
      return data
    } catch {
      toast.error('Upload failed.', { id: toastId })
      return null
    }
  }, [executionId])

  // ── Delete attachment ────────────────────────────────────────────────
  const deleteAttachment = useCallback(async (rowId, attachmentId) => {
    await api.delete(`/executions/${executionId}/rows/${rowId}/attachments/${attachmentId}`)
    setGrouped(prev => prev.map(group => ({
      ...group,
      rows: group.rows.map(row =>
        row.id === rowId
          ? { ...row, attachments: row.attachments.filter(a => a.id !== attachmentId) }
          : row
      )
    })))
    toast.success('Attachment removed.')
  }, [executionId])

  // ── Update execution status ──────────────────────────────────────────
  const updateStatus = useCallback(async (status) => {
    const { data } = await api.patch(`/executions/${executionId}/status`, { status })
    setExecution(prev => ({ ...prev, ...data }))
    toast.success(`Status updated to "${status}".`)
  }, [executionId])

  return {
    execution, grouped, progress, passRate,
    loading, saving,
    saveRow, uploadAttachment, deleteAttachment, updateStatus, reload: load,
  }
}
